/**
 * Approval detail child (`/approvals/$approvalId`) — the reading pane for a
 * specific flagged action. The list panel lives in the parent layout route
 * `approvals.tsx`; this component only supplies the right-pane content.
 *
 * After a decide action the route navigates back to `/approvals` so the
 * index child can auto-select the next pending item.
 */
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { toast } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { meQueryOptions } from "../../../lib/session.ts";
import { type InboxItem, type InboxStatus } from "../../../features/inbox/index.ts";
import { ReadingPane } from "../../../features/inbox/reading-pane.tsx";

export const Route = createFileRoute("/(workspace)/approvals/$approvalId")({
  component: ApprovalDetailRoute,
});

function ApprovalDetailRoute() {
  const { approvalId } = Route.useParams();
  const navigate = useNavigate();

  // Inherit the parent layout route's validated search params.
  const parentSearch = useSearch({ from: "/(workspace)/approvals" });
  const status: InboxStatus = parentSearch.status ?? "pending";

  const me = useQuery(meQueryOptions);
  const isFounder = me.data?.company?.role === "Founder";
  const sellerName =
    me.data?.company?.tradeName?.trim() ||
    me.data?.company?.legalName ||
    "Your company";

  const utils = trpc.useUtils();

  // Hits the parent layout's cached query — no extra network call.
  const list = trpc.approvals.list.useQuery({ limit: 50, status });
  const items = (list.data?.items ?? []) as InboxItem[];
  const item = items.find((i) => i.id === approvalId) ?? null;

  // PR-I: approving a `void_invoice` chains the actual void after the decision.
  const voidInvoice = trpc.invoices.void.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.invoices.list.invalidate(),
        utils.approvals.list.invalidate(),
      ]);
    },
  });

  const decide = trpc.approvals.decide.useMutation({
    onSuccess: async (_data, variables) => {
      await utils.approvals.list.invalidate();

      if (variables.decision === "approved") {
        const approved = items.find((a) => a.id === variables.id);
        if (approved?.kind === "void_invoice" && approved.entityType === "invoice") {
          const reason =
            (approved.requestPayload as { reason?: string }).reason ??
            "Founder approved without note.";
          voidInvoice.mutate({ invoiceId: approved.entityId, reason });
        }
      }

      // Navigate back to the list; the index child auto-selects the next item.
      void navigate({
        to: "/approvals",
        search: status !== "pending" ? { status } : {},
      });
    },
    onError: (err: { message: string }) => {
      toast.error("Couldn't record the decision", { description: err.message });
    },
  });

  function runDecide(decision: "approved" | "rejected", note: string) {
    decide.mutate({ id: approvalId, decision, ...(note ? { note } : {}) });
  }

  return (
    <ReadingPane
      item={item}
      isFounder={isFounder}
      sellerName={sellerName}
      onDecide={runDecide}
      decidePending={decide.isPending}
    />
  );
}
